import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { NotifyAdminSuccessModal } from 'components/quota-components/NotifyAdminSuccessModal';
import { QuotaInfoTooltipContent } from 'components/quota-components/QuotaInfoTooltipContent';
import { Tooltip } from 'components/Tooltip';
import { useIsDataCreditsEnabled } from 'features/quota/hooks/useIsDataCreditsEnabled';
import { useLowQuotaNotificationConfig } from 'features/quota/hooks/useLowQuotaNotificationConfig';
import { useQuota } from 'features/quota/hooks/useQuota';

import { ContactsQuotaTooltip } from '../../ContactsQuotaTooltip';

import { StyledInfoTooltipContent, StyledRevealButton } from './styles';

const INFO_TOOLTIP_MAX_WIDTH = 125;
const QUOTA_TOOLTIP_MAX_WIDTH = 230;

const CONTACTS_COUNT = 1;

export type RevealButtonProps = {
  className?: string;
  isLoading?: boolean;
  onClick?: () => void;
};

export const RevealButton = (props: RevealButtonProps) => {
  const { t } = useTranslation();

  const [isNotifyAdminSuccessModalOpen, setIsNotifyAdminSuccessModalOpen] = useState(false);

  const { quota, canAfford, getRevealCost } = useQuota();

  const { isDataCreditsEnabled } = useIsDataCreditsEnabled();

  const lowQuotaNotificationConfig = useLowQuotaNotificationConfig({
    onNotifyAdminSuccess: () => setIsNotifyAdminSuccessModalOpen(true),
  });

  const renderRevealButton = () => (
    <StyledRevealButton
      data-automation="ContactReveal"
      disabled={quota.isDepleted}
      className={props.className}
      onClick={props.onClick}
      isLoading={props.isLoading}
      variant="primary"
    >
      {t('contact_card.reveal')}
    </StyledRevealButton>
  );

  if (isDataCreditsEnabled) {
    const revealCost = getRevealCost(CONTACTS_COUNT);
    const isRevealAffordable = canAfford(revealCost);

    if (!isRevealAffordable || quota.isDepleted) {
      return (
        <>
          <Tooltip
            placement="top-left"
            content={
              <QuotaInfoTooltipContent
                title={lowQuotaNotificationConfig.title}
                content={lowQuotaNotificationConfig.description}
                buttonText={lowQuotaNotificationConfig.buttonText}
                onButtonClick={lowQuotaNotificationConfig.onButtonClick}
              />
            }
            maxWidth={QUOTA_TOOLTIP_MAX_WIDTH}
          >
            {renderRevealButton()}
          </Tooltip>

          <NotifyAdminSuccessModal
            isOpen={isNotifyAdminSuccessModalOpen}
            onClose={() => setIsNotifyAdminSuccessModalOpen(false)}
          />
        </>
      );
    } else {
      return (
        <Tooltip
          content={
            <StyledInfoTooltipContent>
              {t('contact_card.reveal.cost.tooltip', { cost: revealCost })}
            </StyledInfoTooltipContent>
          }
          maxWidth={INFO_TOOLTIP_MAX_WIDTH}
        >
          {renderRevealButton()}
        </Tooltip>
      );
    }
  } else {
    if (!quota.isDepleted) {
      return renderRevealButton();
    }
  }

  return (
    <ContactsQuotaTooltip placement="left" minWidth={230} location="reveal button">
      {renderRevealButton()}
    </ContactsQuotaTooltip>
  );
};
